/**
 * AttributedQuoteCard: a framed verbatim quote with the speaker's avatar, name, and
 * demographic blurb underneath it. Extracted from FocusGroupSummaryCard (Step 2, prose
 * migration) so the SAME framed-quote visual can render once per focus-group summary
 * (legacy single-quote path) or multiple times, interleaved between prose paragraphs
 * (the new prose branch), without duplicating the markup.
 *
 * PRESENTATIONAL only: the caller resolves attribution and cleans the quote text (e.g.
 * via `resolveQuoteAttribution` / `cleanQuoteText` in FocusGroupSummaryCard). This
 * component never fabricates a name, blurb, or portrait.
 */
import { useState, type ReactNode } from 'react'

import { UserAvatar } from '@/components/ui/UserAvatar'
import { usePersonaThumbFallback } from '@/components/shared/PersonaThumbImg'
import { cn } from '@/lib/utils'

export interface AttributedQuoteCardProps {
  /** The verbatim quote text (already cleaned of any trailing speaker signature). */
  quote: string
  /** Resolved display name of the speaker; "Twin" when unattributed. */
  name: string
  /** Short demographic blurb for the speaker; "" when unknown. */
  blurb: string
  /** Initials fallback for the avatar. */
  initials: string
  /** Portrait for the speaker, when we have one. */
  imageUrl?: string
  /** Override the default test id, useful when several quote cards render on one page. */
  dataTestId?: string
  /**
   * Extra content (version/segment chips, etc) rendered inside the attribution row,
   * after the name/blurb block. Added for the prose-narrative quote render (MessageRow),
   * which carries an item chip + segment chip alongside the speaker. Optional: the legacy
   * FocusGroupSummaryCard usage never passes it, so its render is unaffected.
   */
  chips?: ReactNode
  /**
   * Tighter padding and a smaller avatar, so two framed quotes stacked between prose
   * paragraphs (plus a chart below) don't overwhelm the insight card. Defaults to false:
   * FocusGroupSummaryCard's legacy single-quote usage renders exactly as before.
   */
  compact?: boolean
  /** Override the attribution row's test id. Omitted by default (no data-testid), matching prior behavior. */
  attributionTestId?: string
}

/** Avatar for the featured quote: portrait when present, else themed initials. */
function QuoteAvatar({
  name,
  initials,
  imageUrl,
  compact,
}: {
  name: string
  initials: string
  imageUrl?: string
  compact?: boolean
}) {
  const [broken, setBroken] = useState(false)
  // One-shot fallback (finding 7, image compression): try the pre-generated
  // 64px thumbnail first; if it 404s (not backfilled yet for this persona),
  // retry the original url once before falling back to the initials avatar.
  const { displayUrl, handleImageError } = usePersonaThumbFallback(imageUrl, 64)

  if (displayUrl && !broken) {
    return (
      <img
        src={displayUrl}
        alt={name}
        onError={() => handleImageError(() => setBroken(true))}
        className={cn(
          'shrink-0 rounded-full border border-twin-chat-divider object-cover',
          compact ? 'h-7 w-7' : 'h-9 w-9',
        )}
      />
    )
  }
  return <UserAvatar initials={initials} name={name} size={compact ? 'xs' : 'sm'} />
}

export function AttributedQuoteCard({
  quote,
  name,
  blurb,
  initials,
  imageUrl,
  dataTestId,
  chips,
  compact = false,
  attributionTestId,
}: AttributedQuoteCardProps) {
  return (
    <figure
      className={cn(
        'relative overflow-hidden rounded-xl border border-foreground/10 bg-card-inset',
        compact ? 'mt-3 p-3' : 'mt-4 p-4',
      )}
      data-testid={dataTestId ?? 'qs-focus-group-quote'}
    >
      <blockquote className="relative text-base leading-relaxed text-foreground">
        “{quote}”
      </blockquote>
      {name || blurb || chips ? (
        <div
          className={cn('relative flex flex-wrap items-center gap-2.5', compact ? 'mt-2.5' : 'mt-3')}
          data-testid={attributionTestId}
        >
          <div className="flex items-center gap-2.5">
            <QuoteAvatar name={name} initials={initials} imageUrl={imageUrl} compact={compact} />
            <div className="min-w-0 leading-tight">
              <p className="truncate text-sm font-medium text-foreground">{name}</p>
              {blurb ? <p className="truncate text-xs text-muted-foreground">{blurb}</p> : null}
            </div>
          </div>
          {chips}
        </div>
      ) : null}
    </figure>
  )
}

export default AttributedQuoteCard
